import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';

import { ApiService } from './api.service';

import { Coverage, CoveragePayload, CoverageTab } from 'src/app/models/coverage';
import { CoverageRate, CoverageRatePayload } from 'src/app/models/coverage-rate';
import { NextPanel } from 'src/app/models/next-panel';
import { BenefitSummaryList, CancelBenefit } from 'src/app/models/benefit-summary';
import { KitBenefitCollection, KitBenefitPayload } from 'src/app/models/kit-benefit';
import { AutoTermResponse, CoreBenefitPayload, CoreBenefitResponse } from 'src/app/models/core-benefit';
import { TobaccoModel, TobaccoPayload } from 'src/app/models/tobacco';
import { Eligibility, EligibilityPayloadList } from 'src/app/models/eligibility';
import { AdditionalModuleBase, AdditionalModulePayload } from 'src/app/models/additional-info';
import { PCPModel, PCPPayload } from 'src/app/models/pcp';
import { BeneficiariesResponse, BeneficiaryPayload } from 'src/app/models/beneficiary';
import { CoverageAddDependent } from 'src/app/models/coverage-add-dependent';
import { Disclaimer, DpDisclaimer, FutureBenefitsDisclaimerModel } from 'src/app/models/disclaimer';
import { Dependent } from 'src/app/models/dependent';
import { AutoEnrollResponse, BenefitSelection, BenefitSelectionPayload } from 'src/app/models/benefit';
import { EnrollmentBenefitCategory, EnrollmentWaiveBenefits } from 'src/app/models/enrollment-category';
import { HSAContributionPayload, HsaPeroidContribution } from 'src/app/models/hsa';
import { ToastService } from './toast.service';
import { ToastType } from 'src/app/constants/toast-type';
import { FSAContribution, FSAContributionPayload } from 'src/app/models/fsa';


const controllerRoute = '/enrollment-add';

@Injectable()
export class EnrollmentAddService {

    constructor(
        private apiService: ApiService,
        private toastService: ToastService,
    ) {
    }

    public initializeSerivce(waiveBenefits: EnrollmentWaiveBenefits = null): Observable<NextPanel> {
        if (waiveBenefits)
            return this.apiService.post(`${controllerRoute}/initialize`, waiveBenefits).pipe(
                map((response: NextPanel) => {
                    return response;
                }),
            );
            
        return this.apiService.post(`${controllerRoute}/initialize`, {}).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getBenefits(): Observable<BenefitSelection> {
        return this.apiService.get(`${controllerRoute}/benefits`).pipe(
            map((response: BenefitSelection) => {
                return response;
            }),
        );
    }

    public setBenefits(payload: BenefitSelectionPayload): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/benefits`, payload).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getCoveragePanel(): Observable<CoverageTab> {
        return this.apiService.get(`${controllerRoute}/coverage`).pipe(
            map((response: Coverage) => {
                return response.coverageTabs.slice(-1)[0];
            }),
        );
    }

    public getCoverageSelectionRates(ids: number[], coverageDetailID: number): Observable<CoverageRate> {
        const payload: CoverageRatePayload = new CoverageRatePayload();
        payload.selectedDependentIds = ids;
        payload.coverageDetailID = coverageDetailID ? coverageDetailID : 0;
        return this.apiService.post(`${controllerRoute}/coverage-selection/rates`, payload).pipe(
            map((response: CoverageRate) => {
                return response;
            }),
        );
    }

    public submitCoverageAddDependent(payload: Dependent): Observable<NextPanel> {
        return this.apiService.put(`${controllerRoute}/coverage-selection/add-dependent`, payload).pipe(
            map((response: NextPanel) => {
                this.toastService.buildToastMessage(ToastType.success, '', 'New Dependent Saved!');
                return response;
            }),
        );
    }

    public getHsaContribution(payload: HSAContributionPayload): Observable<HsaPeroidContribution> {
        return this.apiService.post(`${controllerRoute}/coverage-selection/hsa-contribution`, payload).pipe(
            map((response: HsaPeroidContribution) => {
                return response;
            }),
        );
    }

    public getFsaContribution(payload: FSAContributionPayload): Observable<FSAContribution> {
        return this.apiService.post(`${controllerRoute}/coverage-selection/fsa-contribution`, payload).pipe(
            map((response: FSAContribution) => {
                return response;
            }),
        );
    }

    public getCoverageAddDependent(): Observable<CoverageAddDependent> {
        return this.apiService.get(`${controllerRoute}/coverage-add-dependent`).pipe(
            map((response: CoverageAddDependent) => {
                return response;
            }),
        );
    }

    public submitCoveragePanel(payload: CoveragePayload): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/coverage`, payload).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getSummaryPanel(): Observable<BenefitSummaryList> {
        return this.apiService.get(`${controllerRoute}/summary`).pipe(
            map((response: BenefitSummaryList) => {
                return response;
            }),
        );
    }

    public submitSummaryPanel(): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/summary`, null).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getRequestTerm(): Observable<CancelBenefit> {
        return this.apiService.get(`${controllerRoute}/request-term`).pipe(
            map((response: CancelBenefit) => {
                return response;
            }),
        );
    }

    public submitRequestTerm(): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/request-term`, null).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getKitBenefits(): Observable<KitBenefitCollection> {
        return this.apiService.get(`${controllerRoute}/kit-benefits`).pipe(
            map((response: KitBenefitCollection) => {
                return response;
            }),
        );
    }

    public submitKitBenefits(payload: KitBenefitPayload): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/kit-benefits`, payload).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getCoreBenefits(): Observable<CoreBenefitResponse> {
        return this.apiService.get(`${controllerRoute}/core-benefits`).pipe(
            map((response: CoreBenefitResponse) => {
                return response;
            }),
        );
    }

    public submitCoreBenefits(payload: CoreBenefitPayload): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/core-benefits`, payload).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getTobaccoUsage(): Observable<TobaccoModel> {
        return this.apiService.get(`${controllerRoute}/tobacco`).pipe(
            map((response: TobaccoModel) => {
                return response;
            }),
        );
    }

    public submitTobaccoUsage(payload: TobaccoPayload): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/tobacco`, payload).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getEligibility(): Observable<Eligibility> {
        return this.apiService.get(`${controllerRoute}/eligibility`).pipe(
            map((response: Eligibility) => {
                return response;
            }),
        );
    }

    public submitEligibility(payload: EligibilityPayloadList): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/eligibility`, payload).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getAdditionalInfo(): Observable<AdditionalModuleBase> {
        return this.apiService.get(`${controllerRoute}/additional-info`).pipe(
            map((response: AdditionalModuleBase) => {
                return response;
            }),
        );
    }

    public submitAdditionalInfo(payload: AdditionalModulePayload): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/additional-info`, payload).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getFutureBenefits(): Observable<BenefitSelection> {
        return this.apiService.get(`${controllerRoute}/future-benefits`).pipe(
            map((response: BenefitSelection) => {
                return response;
            }),
        );
    }

    public submitFutureBenefits(payload: BenefitSelectionPayload): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/future-benefits`, payload).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getFutureBenefitsDisclaimer(): Observable<FutureBenefitsDisclaimerModel> {
        return this.apiService.get(`${controllerRoute}/future-benefits-disclaimer`).pipe(
            map((response: FutureBenefitsDisclaimerModel) => {
                return response;
            }),
        );
    }

    public submitFutureBenefitsDisclaimer(): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/future-benefits-disclaimer`, null).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getPCP(): Observable<PCPModel> {
        return this.apiService.get(`${controllerRoute}/pcp`).pipe(
            map((response: PCPModel) => {
                return response;
            }),
        );
    }

    public submitPCP(payload: PCPPayload): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/pcp`, payload).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getBeneficiaries(): Observable<BeneficiariesResponse> {
        return this.apiService.get(`${controllerRoute}/beneficiaries`).pipe(
            map((response: BeneficiariesResponse) => {
                return response;
            }),
        );
    }

    public submitBeneficiaries(payload: BeneficiaryPayload): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/beneficiaries`, payload).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getAutoEnrollPanel(): Observable<AutoEnrollResponse> {
        return this.apiService.get(`${controllerRoute}/auto-enroll`).pipe(
            map((response: AutoEnrollResponse) => {
                return response;
            }),
        );
    }

    public submitAutoEnrollPanel(): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/auto-enroll`, null).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getAutoTermPanel(): Observable<AutoTermResponse> {
        return this.apiService.get(`${controllerRoute}/auto-term`).pipe(
            map((response: AutoTermResponse) => {
                return response;
            }),
        );
    }

    public submitAutoTermPanel(): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/auto-term`, null).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getDpDisclaimerPanel(): Observable<DpDisclaimer> {
        return this.apiService.get(`${controllerRoute}/dp-disclaimer`).pipe(
            map((response: DpDisclaimer) => {
                return response;
            }),
        );
    }

    public submitDpDisclaimerPanel(): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/dp-disclaimer`, null).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getDisclaimerPanel(): Observable<Disclaimer> {
        return this.apiService.get(`${controllerRoute}/disclaimer`).pipe(
            map((response: Disclaimer) => {
                return response;
            }),
        );
    }

    public submitDisclaimerPanel(): Observable<NextPanel> {
        return this.apiService.post(`${controllerRoute}/disclaimer`, null).pipe(
            map((response: NextPanel) => {
                return response;
            }),
        );
    }

    public getFinalizePanel(): Observable<EnrollmentBenefitCategory> {
        return this.apiService.get(`${controllerRoute}/finalize`).pipe(
            map((response: EnrollmentBenefitCategory) => {
                return response;
            }),
        );
    }
}